import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import {
  Download, ArrowLeft, Dumbbell, Brain, Target, Zap,
  Calendar, BookOpen, AlertCircle,
} from 'lucide-react';
import {
  TRAINING_PLANS, WEEKLY_MICROCYCLE, GENERAL_GUIDELINES,
  AGE_GROUP, SESSION_DURATION,
} from './trainingPlans';

const ICONS = { tecnica: Target, tactica: Brain, fisico: Zap, mental: BookOpen };

const COLOR_CLASSES = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    dot: 'bg-blue-500' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-500' },
  purple:  { bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  dot: 'bg-purple-500' },
};

function downloadPlanPDF(plan) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;
  const maxW = pageW - margin * 2;
  let y = margin;

  const ensureSpace = (h) => {
    if (y + h > pageH - margin) { doc.addPage(); y = margin; }
  };

  const writeWrapped = (text, size, style, lineH) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text, maxW);
    lines.forEach(line => {
      ensureSpace(lineH);
      doc.text(line, margin, y);
      y += lineH;
    });
  };

  // Header band
  doc.setFillColor(16, 185, 129);
  doc.rect(0, 0, pageW, 70, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('ProKids Talent — Plan de Entrenamiento', margin, 32);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`${AGE_GROUP}  •  Duración: ${SESSION_DURATION}`, margin, 52);

  y = 100;
  doc.setTextColor(17, 24, 39);
  writeWrapped(`Plan ${plan.code} — ${plan.title}`, 20, 'bold', 26);
  y += 4;

  writeWrapped('Objetivo', 12, 'bold', 16);
  writeWrapped(plan.objective, 11, 'normal', 15);
  y += 8;

  writeWrapped('Base científica', 12, 'bold', 16);
  writeWrapped(plan.science, 10, 'italic', 14);
  y += 8;

  writeWrapped(`RPE objetivo: ${plan.targetRPE}`, 11, 'bold', 16);
  y += 10;

  writeWrapped('Bloques de la sesión', 14, 'bold', 20);
  plan.blocks.forEach((b) => {
    ensureSpace(60);
    writeWrapped(`${b.time}  —  ${b.name}`, 11, 'bold', 15);
    writeWrapped(b.content, 10, 'normal', 14);
    doc.setDrawColor(229, 231, 235);
    doc.line(margin, y + 2, pageW - margin, y + 2);
    y += 10;
  });

  y += 6;
  writeWrapped('Controles de carga', 13, 'bold', 18);
  plan.loadLimits.forEach(limit => {
    writeWrapped(`•  ${limit}`, 10, 'normal', 14);
  });

  y += 14;
  ensureSpace(40);
  doc.setDrawColor(209, 213, 219);
  doc.line(margin, y, pageW - margin, y);
  y += 14;
  writeWrapped('Lineamientos generales', 12, 'bold', 16);
  GENERAL_GUIDELINES.forEach(g => writeWrapped(`•  ${g}`, 9, 'normal', 13));

  // Footer on every page
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text(
      `ProKids Talent  •  Generado ${new Date().toLocaleDateString('es-CL')}  •  Página ${i}/${pages}`,
      margin,
      pageH - 20
    );
  }

  const fileName = `plan-entrenamiento-${plan.id}-8-9-anos.pdf`;
  doc.save(fileName);
}

function PlanCard({ plan, onOpen }) {
  const c = COLOR_CLASSES[plan.color];
  const Icon = ICONS[plan.id] ?? Dumbbell;
  return (
    <div className={`bg-white rounded-2xl border ${c.border} p-6 shadow-sm hover:shadow-md transition`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${c.bg} ${c.text}`}>
          <Icon className="w-6 h-6" />
        </div>
        <span className={`text-xs font-bold px-2 py-1 rounded-lg ${c.bg} ${c.text}`}>
          Plan {plan.code}
        </span>
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-1">{plan.title}</h3>
      <p className="text-sm text-gray-600 mb-4">{plan.objective}</p>
      <div className="text-xs text-gray-500 mb-4 flex items-center gap-2">
        <AlertCircle className="w-3.5 h-3.5" />
        RPE objetivo: {plan.targetRPE}
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onOpen(plan)}
          className="flex-1 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2 transition"
        >
          Ver detalle
        </button>
        <button
          onClick={() => downloadPlanPDF(plan)}
          className="flex items-center gap-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-3 py-2 transition"
          title="Descargar PDF"
        >
          <Download className="w-4 h-4" />
          PDF
        </button>
      </div>
    </div>
  );
}

function PlanDetail({ plan, onBack }) {
  const c = COLOR_CLASSES[plan.color];
  const Icon = ICONS[plan.id] ?? Dumbbell;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" />
          Volver a la lista
        </button>
        <button
          onClick={() => downloadPlanPDF(plan)}
          className="flex items-center gap-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-4 py-2 transition"
        >
          <Download className="w-4 h-4" />
          Descargar PDF
        </button>
      </div>

      <div className={`bg-white rounded-2xl border ${c.border} p-6`}>
        <div className="flex items-center gap-4 mb-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${c.bg} ${c.text}`}>
            <Icon className="w-7 h-7" />
          </div>
          <div>
            <div className={`text-xs font-bold ${c.text} mb-1`}>PLAN {plan.code}</div>
            <h2 className="text-2xl font-bold text-gray-900">{plan.title}</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Objetivo</div>
            <div className="text-sm text-gray-800">{plan.objective}</div>
          </div>
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">RPE objetivo</div>
            <div className="text-sm text-gray-800">{plan.targetRPE}</div>
          </div>
        </div>
        <div className="mt-4">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Base científica</div>
          <div className="text-sm text-gray-700 italic">{plan.science}</div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="font-bold text-gray-900 mb-4">Bloques de la sesión ({SESSION_DURATION})</h3>
        <div className="space-y-3">
          {plan.blocks.map((b, i) => (
            <div key={i} className="flex gap-4 pb-3 border-b border-gray-100 last:border-0">
              <div className={`flex-shrink-0 w-2 h-2 rounded-full ${c.dot} mt-2`}></div>
              <div className="flex-1">
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-xs font-mono font-bold text-gray-500">{b.time}</span>
                  <span className="font-semibold text-gray-900">{b.name}</span>
                </div>
                <p className="text-sm text-gray-600">{b.content}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-500" />
          Controles de carga
        </h3>
        <ul className="space-y-2">
          {plan.loadLimits.map((l, i) => (
            <li key={i} className="text-sm text-gray-700 flex gap-2">
              <span className="text-amber-500">•</span> {l}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function TrainingPlansTab() {
  const [selected, setSelected] = useState(null);

  if (selected) return <PlanDetail plan={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Planes de Entrenamiento</h2>
        <p className="text-sm text-gray-500">
          {AGE_GROUP}  •  Sesión de {SESSION_DURATION}  •  Base científica LTAD
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TRAINING_PLANS.map(plan => (
          <PlanCard key={plan.id} plan={plan} onOpen={setSelected} />
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-emerald-600" />
          Microciclo semanal sugerido
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-100">
                <th className="py-2 pr-4">Día</th>
                <th className="py-2 pr-4">Plan</th>
                <th className="py-2">Intensidad</th>
              </tr>
            </thead>
            <tbody>
              {WEEKLY_MICROCYCLE.map((d, i) => (
                <tr key={i} className="border-b border-gray-50 last:border-0">
                  <td className="py-2 pr-4 font-medium text-gray-900">{d.day}</td>
                  <td className="py-2 pr-4 text-gray-700">{d.plan}</td>
                  <td className="py-2 text-gray-500">{d.intensity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6">
        <h3 className="font-bold text-emerald-900 mb-3">Lineamientos generales</h3>
        <ul className="space-y-2">
          {GENERAL_GUIDELINES.map((g, i) => (
            <li key={i} className="text-sm text-emerald-900 flex gap-2">
              <span>•</span> {g}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
